import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { laudosAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Skeleton } from './ui/skeleton';
import { Badge } from './ui/badge';
import { FileSignature, Calendar, ChevronRight } from 'lucide-react';

const MESES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const formatBR = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString('pt-BR');
  } catch {
    return iso;
  }
};

const formatCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const targetForProj = (statusProjeto, projetoId) =>
  statusProjeto === 'arquivado'
    ? `/projetos/arquivados?project=${projetoId}`
    : `/projetos?project=${projetoId}`;

/**
 * Seção "Laudos do Mês" para a página de Relatórios.
 * Mostra projetos com laudos agendados no mês selecionado + lista linear (agenda).
 */
const LaudosMonthSection = () => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await laudosAPI.byMonth(year, month);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const anos = [];
  for (let y = today.getFullYear() - 2; y <= today.getFullYear() + 2; y++) anos.push(y);

  return (
    <Card data-testid="laudos-month-section">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-amber-500" />
              Laudos do Mês
            </CardTitle>
            <CardDescription>
              Projetos e datas de laudos agendados em {MESES[month - 1]}/{year}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="w-36" data-testid="laudos-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MESES.map((m, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-24" data-testid="laudos-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {anos.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : !data || data.total_laudos === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Nenhum laudo agendado neste mês.</p>
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="flex gap-3 flex-wrap">
              <Badge variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-500/30">
                {data.total_laudos} laudo(s)
              </Badge>
              <Badge variant="outline" className="bg-blue-500/10 text-blue-700 border-blue-500/30">
                {data.total_projetos} projeto(s)
              </Badge>
            </div>

            {/* Agenda linear */}
            <div>
              <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                Agenda — 1 linha por laudo
              </h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Projeto</TableHead>
                    <TableHead>Responsável</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.laudos.map((l) => (
                    <TableRow key={l.laudo_id} data-testid={`laudo-row-${l.laudo_id}`}>
                      <TableCell className="font-mono text-sm whitespace-nowrap">{formatBR(l.data)}</TableCell>
                      <TableCell className="font-medium">{l.cliente_nome}</TableCell>
                      <TableCell className="text-sm">{l.descricao}</TableCell>
                      <TableCell className="text-xs">
                        {l.tipo_projeto}
                        {l.numero_interno && <span className="text-muted-foreground"> · {l.numero_interno}</span>}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{l.responsavel_nome || '-'}</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(targetForProj(l.status_projeto, l.projeto_id))}
                          data-testid={`laudo-goto-${l.laudo_id}`}
                        >
                          Ver <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Projetos únicos */}
            <div>
              <h4 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                Projetos com laudos no mês
              </h4>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Nº Interno</TableHead>
                    <TableHead>Valor Crédito</TableHead>
                    <TableHead>Qtd Laudos</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.projetos.map((p) => (
                    <TableRow key={p.id} data-testid={`laudo-proj-${p.id}`}>
                      <TableCell className="font-medium">{p.cliente_nome}</TableCell>
                      <TableCell>{p.tipo_projeto || '-'}</TableCell>
                      <TableCell className="font-mono text-xs">{p.numero_interno || '-'}</TableCell>
                      <TableCell>{formatCurrency(p.valor_credito)}</TableCell>
                      <TableCell>
                        <Badge className="bg-amber-500/10 text-amber-700 border-amber-500/30">
                          {p.qtd_laudos_mes}
                        </Badge>
                      </TableCell>
                      <TableCell className="capitalize text-sm">{p.status || '-'}</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(targetForProj(p.status, p.id))}
                          data-testid={`laudo-goto-proj-${p.id}`}
                        >
                          Abrir <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default LaudosMonthSection;
